import { redirectWithMainEventScope } from '@/app/shared/utils/router-guards/mainEvent';
import {
    composeGuards,
    redirectIfFeatureNotEnabled,
    redirectIfUnauthorized,
} from '@/app/shared/utils/router-guards/auth';

const BASE_PATH = '/venue-mode';

export const venueModeRoutes = [
    // Events
    {
        name: 'VenueModeEventsList',
        path: `${BASE_PATH}/main-events/:mainEventId/events-dashboard`,
        component: () => import('@/app/modules/venueMode/events/EventsList/EventsList.vue'),
        props: true,
        beforeEnter: composeGuards(
            redirectIfFeatureNotEnabled(['venue_mode'], { name: '404' }),
            redirectIfUnauthorized((permissions) => permissions.events.guestOperations.events.list)
        ),
    },
    {
        path: `${BASE_PATH}/main-events`,
        name: 'VenueModeMainEventsList',
        component: () => import('@/app/modules/venueMode/events/MainEventsList/MainEventsList.vue'),
        props: true,
        beforeEnter: composeGuards(
            redirectIfFeatureNotEnabled(['venue_mode'], { name: '404' }),
            redirectIfUnauthorized(
                (permissions) => permissions.events.guestOperations.mainEvents.list
            ),
            redirectWithMainEventScope((mainEventId: any) => ({
                name: 'VenueModeEventsList',
                params: { mainEventId },
            }))
        ),
    },
    {
        path: `${BASE_PATH}/main-events/full-list`,
        name: 'VenueModeMainEventsFullList',
        component: () =>
            import('@/app/modules/venueMode/events/MainEventsFullList/MainEventsFullList.vue'),
        props: true,
        beforeEnter: composeGuards(
            redirectIfFeatureNotEnabled(['venue_mode'], { name: '404' }),
            redirectIfUnauthorized(
                (permissions) => permissions.events.guestOperations.mainEvents.list
            )
        ),
    },
    // Seating
    {
        path: `${BASE_PATH}/seating/main-events`,
        name: 'VenueModeSeatingGuestOperationsMainEventsList',
        component: () =>
            import('@/app/modules/venueMode/seating/MainEventsList/MainEventsList.vue'),
        props: true,
        beforeEnter: composeGuards(
            redirectIfFeatureNotEnabled(['venue_mode'], { name: '404' }),
            redirectIfUnauthorized(
                (permissions) => permissions.seating.guestOperations.mainEventList
            ),
            redirectWithMainEventScope((mainEventId: any) => ({
                name: 'VenueModeSeatingGuestOperationsEventList',
                params: { mainEventId },
            }))
        ),
    },
    {
        path: `${BASE_PATH}/seating/main-events/full-list`,
        name: 'VenueModeSeatingGuestOperationsMainEventsFullList',
        component: () =>
            import('@/app/modules/venueMode/seating/MainEventsFullList/MainEventsFullList.vue'),
        props: true,
        beforeEnter: composeGuards(
            redirectIfFeatureNotEnabled(['venue_mode'], { name: '404' }),
            redirectIfUnauthorized(
                (permissions) => permissions.seating.guestOperations.mainEventList
            )
        ),
    },
    {
        path: `${BASE_PATH}/seating/main-events/:mainEventId/events`,
        name: 'VenueModeSeatingGuestOperationsEventList',
        component: () => import('@/app/modules/venueMode/seating/EventsList/EventsList.vue'),
        props: true,
        beforeEnter: composeGuards(
            redirectIfFeatureNotEnabled(['venue_mode'], { name: '404' }),
            redirectIfUnauthorized((permissions) => permissions.seating.guestOperations.eventList)
        ),
    },
    {
        name: 'VenueModeSeatingGuestOperationsPicker',
        path: `${BASE_PATH}/picker/:mainEventId/:eventId`,
        component: () => import('@/app/modules/venueMode/seating/PickerView/PickerView.vue'),
        props: true,
        beforeEnter: composeGuards(
            redirectIfFeatureNotEnabled(['venue_mode'], { name: '404' }),
            redirectIfUnauthorized((permissions) => permissions.seating.guestOperations.picker)
        ),
        meta: {
            withoutHeader: true,
            withoutInnerContentPadding: true,
        },
    },
    // Guests
    {
        path: `${BASE_PATH}/guest-list/main-events`,
        name: 'VenueModeGuestListMainEventsList',
        component: () =>
            import('@/app/modules/venueMode/guestList/MainEventsList/MainEventsList.vue'),
        props: true,
        beforeEnter: composeGuards(
            redirectIfFeatureNotEnabled(['venue_mode'], { name: '404' }),
            redirectIfUnauthorized((permissions) => permissions.guestList.mainEventsList),
            redirectWithMainEventScope((mainEventId: any) => ({
                name: 'VenueModeGuestListEventsList',
                params: { mainEventId },
            }))
        ),
    },
    {
        path: `${BASE_PATH}/guest-list/main-events/full-list`,
        name: 'VenueModeGuestListMainEventsFullList',
        component: () =>
            import('@/app/modules/venueMode/guestList/MainEventsFullList/MainEventsFullList.vue'),
        props: true,
        beforeEnter: composeGuards(
            redirectIfFeatureNotEnabled(['venue_mode'], { name: '404' }),
            redirectIfUnauthorized((permissions) => permissions.guestList.mainEventsList)
        ),
    },
    {
        path: `${BASE_PATH}/guest-list/main-events/:mainEventId/events`,
        name: 'VenueModeGuestListEventsList',
        component: () => import('@/app/modules/venueMode/guestList/EventsList/EventsList.vue'),
        props: true,
        beforeEnter: composeGuards(
            redirectIfFeatureNotEnabled(['venue_mode'], { name: '404' }),
            redirectIfUnauthorized((permissions) => permissions.guestList.eventsList)
        ),
    },
    {
        path: `${BASE_PATH}/guest-list/main-events/:mainEventId/events/:eventId/guest-list`,
        name: 'VenueModeGuestListEventGuestList',
        component: () =>
            import('@/app/modules/venueMode/guestList/EventGuestList/EventGuestList.vue'),
        props: true,
        beforeEnter: composeGuards(
            redirectIfFeatureNotEnabled(['venue_mode'], { name: '404' }),
            redirectIfUnauthorized((permissions) => permissions.guestList.guestList)
        ),
    },
    // Participant
    {
        path: `${BASE_PATH}/participant/main-events/:mainEventId/profile/:participantId`,
        name: 'VenueModeParticipantProfile',
        component: () => import('@/app/modules/venueMode/search/ParticipantProfile.vue'),
        props: true,
        beforeEnter: composeGuards(
            redirectIfFeatureNotEnabled(['venue_mode'], { name: '404' }),
            redirectIfUnauthorized(
                (permissions) => permissions.bookings.guestOperations.mainEventParticipantsList
            )
        ),
    },
];

export default venueModeRoutes;
